/**
 * The provider stack. The MUI theme is rebuilt whenever `<html>` changes what it says the theme
 * is — the effective colour scheme or the shop look.
 *
 * It reads those two attributes rather than re-deriving the mode from the toggle and the OS,
 * because the attributes already carry a shop's own default scheme and the toggle does not: on a
 * Diamond till with no saved choice the page is dark, and a provider that only asked the toggle
 * would build a light palette under it. Every colour in `components` is a CSS variable anyway;
 * `palette` is the one part MUI has to be handed as hex, and this is where it is handed the
 * right hex.
 */

import { useMemo, type ReactNode } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';

import { useDocumentTheme } from '@pos/shared';

import { buildTheme } from './theme.js';

export function Providers({ children }: { children: ReactNode }) {
  const { mode, shopThemeCode } = useDocumentTheme();
  const theme = useMemo(() => buildTheme(mode, shopThemeCode), [mode, shopThemeCode]);

  return (
    <ThemeProvider theme={theme}>
      {/* MUI's reset. The token stylesheet is imported in main.tsx and wins where they overlap,
          because it is loaded after and both target the same properties. */}
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
